import React from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { initPostHogIfConsented } from "./lib/posthog";
import { isChunkLoadError, tryReloadOnce } from "./lib/chunkErrorRecovery";
import App from "./App";
import i18n, { setLanguage } from "./lib/i18n";
import { isPublicPath, languageFromPath } from "./lib/localizedPaths";
import ErrorBoundary from "./components/shared/ErrorBoundary";
import RootFallback from "./components/shared/RootFallback";
import "./index.css";
import "flag-icons/css/flag-icons.min.css";

// Self-heal stale-chunk failures that happen OUTSIDE the React tree — e.g. a vendor
// chunk (posthog-js below) failing to load after a deploy replaces the hashed asset
// filenames, before App even mounts. ErrorBoundary.tsx handles the same failure mode
// INSIDE the tree (lazy-loaded routes); registered here, first, so it also covers
// anything that throws before ErrorBoundary exists yet. Shares the same cooldown key
// via tryReloadOnce so the two mechanisms never double-reload.
window.addEventListener("error", (event) => {
  if (isChunkLoadError(event.message)) {
    tryReloadOnce();
  }
});
window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason as
    | { message?: string; name?: string }
    | undefined;
  if (isChunkLoadError(reason?.message, reason?.name)) {
    tryReloadOnce();
  }
});

const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    environment: import.meta.env.MODE,
    ignoreErrors: [
      "No Listener: tabs:outgoing.message.ready",
      "Event `CustomEvent` (type=unhandledrejection) captured as promise rejection",
      // Injected by in-app browsers (iOS WKWebView, Android WebView), not our code
      "window.webkit.messageHandlers",
      "Java object is gone",
    ],
  });
}

initPostHogIfConsented();

const rootElement = document.getElementById("root")!;
const app = (
  <React.StrictMode>
    <ErrorBoundary fallback={<RootFallback />}>
      <PostHogProvider client={posthog}>
        <App />
      </PostHogProvider>
    </ErrorBoundary>
  </React.StrictMode>
);

// Public pages have one URL per language ("/pt/..." is Portuguese), and the URL
// decides. A Portuguese visitor on an English public URL was already sent to the
// /pt URL by /language-redirect.js (index.html), before anything was drawn.
if (isPublicPath(window.location.pathname)) {
  setLanguage(languageFromPath(window.location.pathname));
}

// Prerendered routes (scripts/prerender-entry.tsx) ship real markup inside #root, so
// must hydrate onto it rather than re-render from scratch. Routes that only ever get
// the plain SPA shell (e.g. authenticated app routes) have an empty #root and mount as
// before. Each prerendered page is in its URL's language, so it only hydrates when
// the active language matches (always, on a public page, after the line above).
if (
  rootElement.hasChildNodes() &&
  i18n.language === languageFromPath(window.location.pathname)
) {
  hydrateRoot(rootElement, app);
} else {
  createRoot(rootElement).render(app);
}
